import { db } from './firebase';
import {
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  deleteDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import type { PortalDocumentMarkupStroke, DocumentMarkupPoint, DocumentMarkupTool } from '../types';

const COLLECTION = 'portalDocumentMarkup';

function fromFirestore(snap: { id: string; data: () => Record<string, unknown> }): PortalDocumentMarkupStroke {
  const d = snap.data();
  return {
    id: snap.id,
    portalId: String(d.portalId || ''),
    dealId: String(d.dealId || ''),
    releaseId: String(d.releaseId || ''),
    bundleId: typeof d.bundleId === 'string' ? d.bundleId : undefined,
    documentId: String(d.documentId || ''),
    documentUrl: String(d.documentUrl || ''),
    pageNumber: Number(d.pageNumber) || 1,
    tool: (d.tool as DocumentMarkupTool) || 'pen',
    color: String(d.color || '#3b82f6'),
    strokeWidth: Number(d.strokeWidth) || 2,
    points: (d.points as DocumentMarkupPoint[]) || [],
    authorUid: String(d.authorUid || ''),
    authorName: String(d.authorName || ''),
    createdAt: (d.createdAt as { toDate?: () => Date })?.toDate?.() ?? new Date(),
  };
}

export function subscribePortalMarkup(
  documentId: string,
  callback: (strokes: PortalDocumentMarkupStroke[]) => void,
): () => void {
  const q = query(collection(db, COLLECTION), where('documentId', '==', documentId));
  return onSnapshot(
    q,
    snap => callback(snap.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() }))),
    err => console.warn('[portalDocumentMarkup] subscribe failed', err),
  );
}

export async function createPortalMarkupStroke(
  input: Omit<PortalDocumentMarkupStroke, 'id' | 'createdAt'>,
): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTION), {
    ...input,
    createdAt: Timestamp.now(),
  });
  return ref.id;
}

export async function deletePortalMarkupStroke(strokeId: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, strokeId));
}

export function portalMarkupStrokeToDocumentStroke(
  stroke: PortalDocumentMarkupStroke,
): import('../types').DocumentMarkupStroke {
  return {
    id: stroke.id,
    documentId: stroke.documentId,
    documentUrl: stroke.documentUrl,
    leadId: stroke.dealId,
    pageNumber: stroke.pageNumber,
    tool: stroke.tool,
    color: stroke.color,
    strokeWidth: stroke.strokeWidth,
    points: stroke.points,
    userId: stroke.authorUid,
    userName: stroke.authorName,
    createdAt: stroke.createdAt,
  };
}

export function portalAnnotationToPdfAnnotation(
  a: import('../types').PortalPdfAnnotation,
): import('../types').PdfAnnotation {
  return {
    id: a.id,
    documentId: a.documentId,
    documentUrl: a.documentUrl,
    leadId: a.dealId,
    pageNumber: a.pageNumber,
    x: a.x,
    y: a.y,
    content: a.content,
    userId: a.authorUid,
    userName: a.authorName,
    mentions: a.mentions,
    replies: a.replies,
    reactions: a.reactions,
    createdAt: a.createdAt,
  };
}
